import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Architecture & Structures — 8 ANIMATED templates
   bridge_forces, skyscraper_sway, arch_physics,
   dome_pressure, pyramid_build, aqueduct_flow,
   windmill_spin, lighthouse_beam
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Bridge Forces ────────────────────────────────────── */
export const BridgeForces: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Bridge Engineering";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const phase = (props.phase as number) || 0;
  const parts = (props.parts as string[]) || ["Deck", "Cable", "Tower", "Foundation"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Water */}
      <rect x="0" y="420" width="800" height="180" fill="#0d47a1" opacity="0.15" />
      <path d="M0,420 Q100,410 200,420 Q300,430 400,420 Q500,410 600,420 Q700,430 800,420" fill="none" stroke="#42a5f5" strokeWidth="1.5" opacity="0.2">
        <animate attributeName="d" values="M0,420 Q100,410 200,420 Q300,430 400,420 Q500,410 600,420 Q700,430 800,420;M0,420 Q100,430 200,420 Q300,410 400,420 Q500,430 600,420 Q700,410 800,420;M0,420 Q100,410 200,420 Q300,430 400,420 Q500,410 600,420 Q700,430 800,420" dur="4s" repeatCount="indefinite" />
      </path>
      {/* Towers */}
      {[250, 550].map((x, i) => (
        <rect key={i} x={x - 15} y="180" width="30" height="240" rx="4"
          fill={activeIndex === 2 ? "#f9a825" : "#546e7a"} stroke="#455a64" strokeWidth="2">
          {activeIndex === 2 && <animate attributeName="fill" values="#546e7a;#f9a825;#546e7a" dur="1.5s" repeatCount="indefinite" />}
        </rect>
      ))}
      {/* Bridge deck */}
      <rect x="80" y="340" width="640" height="16" rx="3"
        fill={activeIndex === 0 ? "#f9a825" : "#bdbdbd"} stroke="#9e9e9e" strokeWidth="1" />
      {/* Cables */}
      {[130, 190, 310, 370, 430, 490, 610, 670].map((x, i) => (
        <line key={i} x1={i < 4 ? 250 : 550} y1="185" x2={x} y2="340"
          stroke={activeIndex === 1 ? "#f9a825" : "#bdbdbd"} strokeWidth="1.5" opacity="0.6">
          {activeIndex === 1 && <animate attributeName="opacity" values="0.4;0.9;0.4" dur={`${1 + i * 0.1}s`} repeatCount="indefinite" />}
        </line>
      ))}
      {/* Main cables */}
      <path d="M80,340 Q250,160 400,340" fill="none" stroke="#bdbdbd" strokeWidth="3" />
      <path d="M400,340 Q550,160 720,340" fill="none" stroke="#bdbdbd" strokeWidth="3" />
      {/* Force arrows */}
      <g opacity="0.5">
        {/* Weight */}
        <line x1="400" y1="360" x2="400" y2="400" stroke="#e74c3c" strokeWidth="3" markerEnd="url(#bf-down)">
          <animate attributeName="y2" values="400;410;400" dur={`${1.5 + Math.sin(phase) * 0.3}s`} repeatCount="indefinite" />
        </line>
        <text x="420" y="395" fill="#e74c3c" fontSize="11">Weight</text>
        {/* Tension */}
        <line x1="250" y1="175" x2="250" y2="145" stroke="#4caf50" strokeWidth="3" markerEnd="url(#bf-up)" />
        <text x="270" y="155" fill="#4caf50" fontSize="11">Tension</text>
        {/* Compression */}
        <line x1="550" y1="180" x2="550" y2="410" stroke="#42a5f5" strokeWidth="2" strokeDasharray="6,4" />
        <text x="570" y="300" fill="#42a5f5" fontSize="11">Compression</text>
      </g>
      <defs>
        <marker id="bf-down" viewBox="0 0 10 10" refX="5" refY="0" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L5,10 L10,0Z" fill="#e74c3c" />
        </marker>
        <marker id="bf-up" viewBox="0 0 10 10" refX="5" refY="10" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,10 L5,0 L10,10Z" fill="#4caf50" />
        </marker>
      </defs>
      {/* Labels */}
      {parts.map((p, i) => (
        <text key={i} x={100 + i * 170} y="470" textAnchor="middle"
          fill={i === activeIndex ? "#f9a825" : "#7f8c8d"} fontSize="11">{p}</text>
      ))}
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Skyscraper Sway ──────────────────────────────────── */
export const SkyscraperSway: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Skyscraper Engineering";
  const phase = (props.phase as number) || 0;
  const sway = Math.sin(phase * 0.5) * 8;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Ground */}
      <rect x="0" y="520" width="800" height="80" fill="#37474f" opacity="0.3" />
      {/* Building */}
      <g transform={`skewX(${sway * 0.3})`}>
        <rect x="330" y="80" width="140" height="440" rx="4" fill="#1c2833" stroke="#2c3e50" strokeWidth="2" />
        {/* Floors */}
        {Array.from({ length: 22 }, (_, i) => (
          <g key={i}>
            <line x1="332" y1={90 + i * 20} x2="468" y2={90 + i * 20} stroke="#2c3e50" strokeWidth="0.5" />
            {/* Windows */}
            {[340, 370, 400, 430].map((wx) => (
              <rect key={wx} x={wx} y={93 + i * 20} width="20" height="14" rx="1"
                fill={Math.random() > 0.3 ? "#2196f3" : "#0d1117"} opacity="0.3">
                <animate attributeName="opacity" values="0.2;0.4;0.2" dur={`${2 + i * 0.1}s`} repeatCount="indefinite" />
              </rect>
            ))}
          </g>
        ))}
        {/* Antenna */}
        <line x1="400" y1="80" x2="400" y2="40" stroke="#bdbdbd" strokeWidth="2" />
        <circle cx="400" cy="38" r="3" fill="#e74c3c">
          <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
        </circle>
      </g>
      {/* Wind arrows */}
      {[120, 200, 300, 400].map((y, i) => (
        <line key={i} x1={150 + i * 10} y1={y} x2={280 + i * 10} y2={y}
          stroke="#ecf0f1" strokeWidth="1.5" opacity="0.15" markerEnd="url(#ss-wind)">
          <animate attributeName="x1" from={150 + i * 10} to={200 + i * 10} dur="2s" repeatCount="indefinite" />
          <animate attributeName="x2" from={280 + i * 10} to={330 + i * 10} dur="2s" repeatCount="indefinite" />
        </line>
      ))}
      <defs>
        <marker id="ss-wind" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#ecf0f1" />
        </marker>
      </defs>
      {/* Sway indicator */}
      <g transform={`translate(600, 280)`}>
        <line x1="0" y1="-40" x2="0" y2="40" stroke="#2c3e50" strokeWidth="1" />
        <line x1="-20" y1="-40" x2="20" y2="-40" stroke="#2c3e50" strokeWidth="1" />
        <circle cx={sway * 2} cy="0" r="6" fill="#f9a825">
          <animate attributeName="opacity" values="0.7;1;0.7" dur="1s" repeatCount="indefinite" />
        </circle>
        <text x="0" y="60" textAnchor="middle" fill="#7f8c8d" fontSize="10">Sway Meter</text>
      </g>
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Arch Physics ─────────────────────────────────────── */
export const ArchPhysics: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Arch Forces";
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Arch */}
      <path d="M150,420 Q150,120 400,120 Q650,120 650,420" fill="none" stroke="#bdbdbd" strokeWidth="12" strokeLinecap="round" />
      {/* Keystone */}
      <rect x="378" y="110" width="44" height="40" rx="4"
        fill={activeIndex === 0 ? "#f9a825" : "#546e7a"} stroke="#455a64" strokeWidth="2">
        {activeIndex === 0 && <animate attributeName="fill" values="#546e7a;#f9a825;#546e7a" dur="1.5s" repeatCount="indefinite" />}
      </rect>
      {/* Voussoirs (arch stones) */}
      {Array.from({ length: 10 }, (_, i) => {
        const t = (i + 0.5) / 11;
        const angle = Math.PI * (1 - t);
        const cx = 400 + Math.cos(angle) * 250;
        const cy = 420 - Math.sin(angle) * 300;
        return (
          <rect key={i} x={cx - 18} y={cy - 12} width="36" height="24" rx="3"
            fill="#546e7a" stroke="#455a64" strokeWidth="1"
            transform={`rotate(${(0.5 - t) * 180}, ${cx}, ${cy})`} opacity="0.6" />
        );
      })}
      {/* Force arrows — compression through arch */}
      {[0.15, 0.3, 0.5, 0.7, 0.85].map((t, i) => {
        const angle = Math.PI * (1 - t);
        const cx = 400 + Math.cos(angle) * 250;
        const cy = 420 - Math.sin(angle) * 300;
        const dx = Math.cos(angle + Math.PI / 2) * 30;
        const dy = -Math.sin(angle + Math.PI / 2) * 30;
        return (
          <line key={i} x1={cx} y1={cy} x2={cx + dx} y2={cy + dy}
            stroke="#4caf50" strokeWidth="2" opacity="0.5">
            <animate attributeName="opacity" values="0.3;0.7;0.3" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Weight on top */}
      <line x1="400" y1="80" x2="400" y2="108" stroke="#e74c3c" strokeWidth="3" markerEnd="url(#ap-d)">
        <animate attributeName="y2" values="108;115;108" dur="1.5s" repeatCount="indefinite" />
      </line>
      <text x="420" y="90" fill="#e74c3c" fontSize="12">Load</text>
      {/* Foundation reactions */}
      {[150, 650].map((x) => (
        <g key={x}>
          <rect x={x - 20} y="420" width="40" height="50" fill="#37474f" />
          <line x1={x} y1="470" x2={x} y2="440" stroke="#4caf50" strokeWidth="2" markerEnd="url(#ap-u)" />
        </g>
      ))}
      <defs>
        <marker id="ap-d" viewBox="0 0 10 10" refX="5" refY="0" markerWidth="5" markerHeight="5" orient="auto"><path d="M0,0 L5,10 L10,0Z" fill="#e74c3c" /></marker>
        <marker id="ap-u" viewBox="0 0 10 10" refX="5" refY="10" markerWidth="5" markerHeight="5" orient="auto"><path d="M0,10 L5,0 L10,10Z" fill="#4caf50" /></marker>
      </defs>
      <text x="400" y="230" textAnchor="middle" fill="#4caf50" fontSize="12" opacity="0.6">Compression Path</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Dome Pressure ────────────────────────────────────── */
export const DomePressure: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Dome Structure";
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Dome */}
      <path d="M150,400 Q150,100 400,100 Q650,100 650,400" fill="none" stroke="#bdbdbd" strokeWidth="4" />
      {/* Meridian lines */}
      {[-0.4, -0.2, 0, 0.2, 0.4].map((offset, i) => (
        <path key={i} d={`M${400 + offset * 350},400 Q${400 + offset * 200},${200 + Math.abs(offset) * 50} 400,100`}
          fill="none" stroke="#546e7a" strokeWidth="1.5" opacity="0.4" />
      ))}
      {/* Horizontal rings */}
      {[200, 280, 350].map((y, i) => {
        const w = (y - 100) / 300 * 250;
        return (
          <ellipse key={i} cx="400" cy={y} rx={w} ry={15 + i * 5}
            fill="none" stroke="#546e7a" strokeWidth="1.5" opacity="0.3" />
        );
      })}
      {/* Stress visualization — animated flowing lines */}
      {Array.from({ length: 8 }, (_, i) => {
        const angle = (i * 45) * Math.PI / 180;
        const startX = 400 + Math.cos(angle) * 50;
        const startY = 140;
        const endX = 400 + Math.cos(angle) * 220;
        const endY = 380;
        return (
          <line key={i} x1={startX} y1={startY} x2={endX} y2={endY}
            stroke="#4caf50" strokeWidth="1.5" opacity="0.2" strokeDasharray="8,8">
            <animate attributeName="stroke-dashoffset" from="16" to="0" dur={`${1.5 + i * 0.1}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Top keystone */}
      <circle cx="400" cy="100" r="12" fill="#f9a825" opacity="0.7">
        <animate attributeName="r" values="12;14;12" dur={`${2 + Math.sin(phase) * 0.3}s`} repeatCount="indefinite" />
      </circle>
      {/* Base line */}
      <line x1="140" y1="400" x2="660" y2="400" stroke="#546e7a" strokeWidth="3" />
      {/* Force arrows along dome */}
      {[0.2, 0.4, 0.6, 0.8].map((t) => {
        const y = 100 + t * 300;
        const x = 400 + (t * 250) * (t > 0.5 ? 1 : -1);
        return (
          <g key={t} opacity="0.4">
            <circle cx={x} cy={y} r="4" fill="#42a5f5">
              <animate attributeName="r" values="4;6;4" dur="1.5s" repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}
      <text x="400" y="460" textAnchor="middle" fill="#4caf50" fontSize="12" opacity="0.6">Forces flow to base</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Pyramid Construction ─────────────────────────────── */
export const PyramidBuild: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Pyramid Construction";
  const animationProgress = (props.animationProgress as number) || 0;
  const layers = 8;
  const visibleLayers = Math.ceil(animationProgress * layers);
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Sand / ground */}
      <rect x="0" y="460" width="800" height="140" fill="#f9a825" opacity="0.08" />
      {/* Pyramid layers building up */}
      {Array.from({ length: layers }, (_, i) => {
        if (i >= visibleLayers) return null;
        const y = 460 - (i + 1) * 40;
        const halfW = 200 - i * 22;
        return (
          <rect key={i} x={400 - halfW} y={y} width={halfW * 2} height="38" rx="2"
            fill={`hsl(40, 50%, ${25 + i * 3}%)`} stroke={`hsl(40, 40%, ${20 + i * 3}%)`} strokeWidth="1"
            opacity={i === visibleLayers - 1 ? 0.8 : 1}>
            {i === visibleLayers - 1 && (
              <animate attributeName="opacity" values="0.5;1" dur="0.5s" fill="freeze" />
            )}
          </rect>
        );
      })}
      {/* Block being placed (if building) */}
      {visibleLayers < layers && visibleLayers > 0 && (
        <g>
          <rect x="500" y={460 - visibleLayers * 40 - 20} width="40" height="25" rx="3" fill="#d4a44a" opacity="0.7">
            <animate attributeName="x" from="600" to={400 - (200 - visibleLayers * 22)} dur="2s" repeatCount="indefinite" />
          </rect>
          {/* Workers/ramp hint */}
          <line x1="700" y1="460" x2={400 + 200 - visibleLayers * 22} y2={460 - visibleLayers * 40}
            stroke="#8d6e63" strokeWidth="2" strokeDasharray="6,4" opacity="0.3" />
        </g>
      )}
      {/* Capstone glow */}
      {visibleLayers >= layers && (
        <polygon points="400,140 370,180 430,180" fill="#f9a825" opacity="0.6">
          <animate attributeName="opacity" values="0.4;0.8;0.4" dur="2s" repeatCount="indefinite" />
        </polygon>
      )}
      {/* Sun */}
      <circle cx="650" cy="80" r="40" fill="#fff9c4" opacity="0.2">
        <animate attributeName="r" values="40;44;40" dur="3s" repeatCount="indefinite" />
      </circle>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Aqueduct Flow ────────────────────────────────────── */
export const AqueductFlow: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Aqueduct";
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Arches */}
      {[0, 1, 2, 3, 4].map((i) => {
        const x = 90 + i * 150;
        return (
          <g key={i}>
            {/* Pillars */}
            <rect x={x - 15} y="250" width="15" height="200" fill="#8d6e63" stroke="#6d4c41" strokeWidth="1" />
            <rect x={x + 120} y="250" width="15" height="200" fill="#8d6e63" stroke="#6d4c41" strokeWidth="1" />
            {/* Arch */}
            <path d={`M${x},250 Q${x + 60},160 ${x + 120},250`}
              fill="none" stroke="#8d6e63" strokeWidth="8" />
          </g>
        );
      })}
      {/* Water channel on top */}
      <rect x="60" y="218" width="680" height="30" rx="4" fill="#8d6e63" stroke="#6d4c41" strokeWidth="2" />
      {/* Water flowing */}
      <rect x="65" y="222" width="670" height="12" rx="3" fill="#1565c0" opacity="0.3" />
      {/* Animated water flow */}
      {flowProgress > 0 && Array.from({ length: 8 }, (_, i) => (
        <circle key={i} cx={80 + i * 80} cy="228" r="4" fill="#42a5f5" opacity="0.4">
          <animate attributeName="cx" from={80} to={740} dur="4s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.4;0.2;0.4" dur="4s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Ground */}
      <line x1="40" y1="450" x2="760" y2="450" stroke="#5d4037" strokeWidth="3" />
      {/* Gravity arrows */}
      <line x1="120" y1="200" x2="680" y2="220" stroke="#546e7a" strokeWidth="1" strokeDasharray="8,6" />
      <text x="400" y="195" textAnchor="middle" fill="#7f8c8d" fontSize="11">Slight downward slope (gravity-fed)</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Windmill Spin ────────────────────────────────────── */
export const WindmillSpin: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Windmill";
  const rotation = (props.rotation as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="wm-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#42a5f5" /><stop offset="100%" stopColor="#90caf9" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#wm-sky)" rx="16" />
      {/* Ground */}
      <rect x="0" y="460" width="800" height="140" fill="#2e7d32" opacity="0.4" />
      {/* Windmill body */}
      <polygon points="360,200 440,200 450,460 350,460" fill="#8d6e63" stroke="#6d4c41" strokeWidth="2" />
      {/* Door */}
      <rect x="385" y="400" width="30" height="55" rx="4" fill="#5d4037" />
      {/* Windows */}
      <circle cx="400" cy="300" r="15" fill="#42a5f5" opacity="0.3" stroke="#6d4c41" strokeWidth="1" />
      <circle cx="400" cy="350" r="12" fill="#42a5f5" opacity="0.3" stroke="#6d4c41" strokeWidth="1" />
      {/* Blades */}
      <g transform={`rotate(${rotation}, 400, 200)`}>
        {[0, 90, 180, 270].map((angle) => (
          <g key={angle} transform={`rotate(${angle}, 400, 200)`}>
            <path d="M400,200 L395,80 Q400,60 405,80 L400,200" fill="#ecf0f1" stroke="#bdbdbd" strokeWidth="1" />
            {/* Lattice */}
            {[100, 130, 160].map((y) => (
              <line key={y} x1="397" y1={y} x2="403" y2={y} stroke="#bdbdbd" strokeWidth="0.5" />
            ))}
          </g>
        ))}
        <circle cx="400" cy="200" r="10" fill="#6d4c41" stroke="#5d4037" strokeWidth="2" />
      </g>
      {/* Wind lines */}
      {[180, 230, 280].map((y, i) => (
        <line key={i} x1={180 - i * 20} y1={y} x2={300 - i * 20} y2={y}
          stroke="#ecf0f1" strokeWidth="1" opacity="0.15" strokeDasharray="12,8">
          <animate attributeName="x1" from={180 - i * 20} to={280 - i * 20} dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          <animate attributeName="x2" from={300 - i * 20} to={400 - i * 20} dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
        </line>
      ))}
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Lighthouse Beam ──────────────────────────────────── */
export const LighthouseBeam: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Lighthouse";
  const rotation = (props.rotation as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Sea */}
      <rect x="0" y="420" width="800" height="180" fill="#0d47a1" opacity="0.2" />
      {/* Waves */}
      <path d="M0,430 Q50,420 100,430 Q150,440 200,430 Q250,420 300,430 Q350,440 400,430 Q450,420 500,430 Q550,440 600,430 Q650,420 700,430 Q750,440 800,430"
        fill="none" stroke="#42a5f5" strokeWidth="1.5" opacity="0.2">
        <animate attributeName="d" values="M0,430 Q50,420 100,430 Q150,440 200,430 Q250,420 300,430 Q350,440 400,430 Q450,420 500,430 Q550,440 600,430 Q650,420 700,430 Q750,440 800,430;M0,430 Q50,440 100,430 Q150,420 200,430 Q250,440 300,430 Q350,420 400,430 Q450,440 500,430 Q550,420 600,430 Q650,440 700,430 Q750,420 800,430;M0,430 Q50,420 100,430 Q150,440 200,430 Q250,420 300,430 Q350,440 400,430 Q450,420 500,430 Q550,440 600,430 Q650,420 700,430 Q750,440 800,430" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Island/rock */}
      <ellipse cx="400" cy="430" rx="100" ry="25" fill="#37474f" />
      {/* Lighthouse body */}
      <polygon points="380,200 420,200 430,420 370,420" fill="#ecf0f1" stroke="#bdbdbd" strokeWidth="2" />
      {/* Red stripes */}
      {[260, 320, 380].map((y) => (
        <rect key={y} x={375 + (y - 200) * 0.05} y={y} width={50 - (y - 200) * 0.1} height="25" fill="#e74c3c" opacity="0.6" />
      ))}
      {/* Lamp room */}
      <rect x="375" y="175" width="50" height="30" rx="4" fill="#1c2833" stroke="#bdbdbd" strokeWidth="2" />
      <rect x="380" y="178" width="40" height="24" rx="3" fill="#fff9c4" opacity="0.6" />
      {/* Dome */}
      <path d="M375,175 Q400,155 425,175" fill="#546e7a" stroke="#455a64" strokeWidth="1" />
      {/* Rotating beam */}
      <g transform={`rotate(${rotation}, 400, 190)`}>
        {/* Right beam */}
        <polygon points="430,185 800,160 800,220 430,195" fill="#fff9c4" opacity="0.08" />
        {/* Left beam */}
        <polygon points="370,185 0,160 0,220 370,195" fill="#fff9c4" opacity="0.08" />
      </g>
      {/* Stars */}
      {[100, 200, 600, 700, 150, 550].map((x, i) => (
        <circle key={i} cx={x} cy={50 + (i % 3) * 40} r="1.5" fill="#ecf0f1" opacity="0.3">
          <animate attributeName="opacity" values="0.1;0.4;0.1" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
